/**
 * Thin wrapper over the Google Sheets v4 REST API.
 *
 * Every call carries the signed-in user's OAuth token, reads included. The
 * earlier design read with an API key, which only works on publicly readable
 * spreadsheets; this one is private, so an API key read returns 403.
 *
 * Nothing above this layer knows the Sheets API exists.
 */
import { config } from "../config";
import type { CellValue } from "./schema";

const BASE = "https://sheets.googleapis.com/v4/spreadsheets";

export class SheetsError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly detail?: unknown
  ) {
    super(message);
    this.name = "SheetsError";
  }
}

function describe(status: number, detail: unknown): string {
  const raw =
    detail && typeof detail === "object" && "error" in detail
      ? ((detail as { error?: { message?: string } }).error?.message ?? "")
      : "";
  switch (status) {
    case 400:
      return raw
        ? `The sheet rejected the request: ${raw}`
        : "The sheet rejected the request. A value may not match the Status dropdown.";
    case 401:
      return "Your Google session expired. Connect again to continue.";
    case 403:
      return "This account cannot open the sheet. Check that it is shared with you and that the OAuth client is allowed on this origin.";
    case 404:
      return "The spreadsheet or tab was not found. Check VITE_SHEET_ID and the tab names.";
    case 429:
      return "Google rate-limited the request. Wait a moment and try again.";
    default:
      return `The sheet request failed (${status}).`;
  }
}

async function request<T>(
  path: string,
  token: string,
  init: RequestInit = {}
): Promise<T> {
  const response = await fetch(`${BASE}/${config.sheetId}${path}`, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      ...(init.headers ?? {}),
    },
  });

  if (!response.ok) {
    const detail = await response.json().catch(() => undefined);
    throw new SheetsError(describe(response.status, detail), response.status, detail);
  }
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

interface ValueRange {
  range?: string;
  values?: unknown[][];
}

export interface RangeWrite {
  range: string;
  values: CellValue[];
}

export const sheets = {
  /**
   * Read a range as raw values.
   *
   * UNFORMATTED_VALUE keeps dates as serial numbers rather than localised
   * display strings. See lib/dates.ts for why that matters.
   */
  async read(range: string, token: string): Promise<unknown[][]> {
    const data = await request<ValueRange>(
      `/values/${encodeURIComponent(range)}` +
        `?valueRenderOption=UNFORMATTED_VALUE&dateTimeRenderOption=SERIAL_NUMBER`,
      token
    );
    return data.values ?? [];
  },

  /** Read several ranges in one round trip, in the order given. */
  async readMany(ranges: string[], token: string): Promise<unknown[][][]> {
    const query = ranges
      .map((r) => `ranges=${encodeURIComponent(r)}`)
      .join("&");
    const data = await request<{ valueRanges?: ValueRange[] }>(
      `/values:batchGet?${query}` +
        `&valueRenderOption=UNFORMATTED_VALUE&dateTimeRenderOption=SERIAL_NUMBER`,
      token
    );
    return (data.valueRanges ?? []).map((v) => v.values ?? []);
  },

  /**
   * Write one or more ranges atomically.
   *
   * RAW, not USER_ENTERED: dates are sent as serial numbers and must not be
   * re-parsed, and a task title that happens to start with "=" should stay
   * text rather than becoming a formula.
   */
  async batchWrite(updates: RangeWrite[], token: string): Promise<void> {
    if (!updates.length) return;
    await request("/values:batchUpdate", token, {
      method: "POST",
      body: JSON.stringify({
        valueInputOption: "RAW",
        data: updates.map((u) => ({ range: u.range, values: [u.values] })),
      }),
    });
  },

  /** Append a row after the last populated row of the range's table. */
  async append(range: string, values: CellValue[], token: string): Promise<number> {
    const data = await request<{ updates?: { updatedRange?: string } }>(
      `/values/${encodeURIComponent(range)}:append` +
        `?valueInputOption=RAW&insertDataOption=INSERT_ROWS&includeValuesInResponse=false`,
      token,
      { method: "POST", body: JSON.stringify({ values: [values] }) }
    );
    const updated = data.updates?.updatedRange ?? "";
    const match = updated.match(/![A-Z]+(\d+)/);
    return match ? Number(match[1]) : -1;
  },

  /** Tab titles currently in the spreadsheet. */
  async tabTitles(token: string): Promise<string[]> {
    const data = await request<{
      sheets?: { properties?: { title?: string } }[];
    }>("?fields=sheets.properties.title", token);
    return (data.sheets ?? [])
      .map((s) => s.properties?.title ?? "")
      .filter(Boolean);
  },

  /** Create a tab. Safe to call only after checking tabTitles. */
  async addTab(title: string, token: string): Promise<void> {
    await request(":batchUpdate", token, {
      method: "POST",
      body: JSON.stringify({
        requests: [{ addSheet: { properties: { title } } }],
      }),
    });
  },
};
